import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { APP_VERSION, BUILD_TIMESTAMP } from './config';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

import Login from './pages/Login';
import Events from './pages/Events';
import CreateEvent from './pages/CreateEvent';
import EditEvent from './pages/EditEvent';
import EventDetail from './pages/EventDetail';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import CookiePolicy from './pages/CookiePolicy';
import FAQ from './pages/FAQ';
import LoadingSpinner from './components/LoadingSpinner';

// Protected Route component for creator/admin only access
const ProtectedRoute = ({ children, allowedRoles = ['creator', 'admin'] }) => {
  const { user } = useAuth();
  
  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-4xl mb-4">🚫</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Access Denied</h3>
          <p className="text-gray-600 dark:text-gray-400">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }
  
  return children;
};

function App() {
  const { loading, isAuthenticated } = useAuth();

  // Add cache-busting meta tags
  useEffect(() => {
    // Update meta tags for cache busting
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'version');
    meta.setAttribute('content', `${APP_VERSION}-${BUILD_TIMESTAMP}`);
    document.head.appendChild(meta);

    // Add cache control headers for development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Development mode - Cache busting enabled');
      console.log(`📦 App Version: ${APP_VERSION}`);
      console.log(`⏰ Build Timestamp: ${BUILD_TIMESTAMP}`);
    }

    return () => {
      // Cleanup meta tag on unmount
      const existingMeta = document.querySelector('meta[name="version"]');
      if (existingMeta) {
        existingMeta.remove();
      }
    };
  }, []);

  return (
    <ThemeProvider>
      {loading ? (
        <LoadingSpinner />
      ) : !isAuthenticated ? (
        <Login />
      ) : (
        <div className="min-h-screen relative">
          {/* Background Image */}
          <div
            className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{ backgroundImage: 'url(https://i.ibb.co/nsR3vLRB/decades-wallpaper.png)' }}
          />

          {/* Dark overlay */}
          <div className="fixed inset-0 bg-gray-900/20" />

          {/* Content */}
          <div className="relative z-10 flex flex-col min-h-screen">
            <Navbar />
            <main className="container mx-auto px-4 py-8 flex-grow">
              <Routes>
                <Route path="/" element={<Navigate to="/events" replace />} />
                <Route path="/events" element={<Events />} />
                <Route path="/events/:eventId" element={<EventDetail />} />
                <Route path="/events/:eventId/edit" element={
                  <ProtectedRoute allowedRoles={['creator', 'admin']}>
                    <EditEvent />
                  </ProtectedRoute>
                } />
                <Route path="/create-event" element={
                  <ProtectedRoute allowedRoles={['creator', 'admin']}>
                    <CreateEvent />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={<Profile />} />
                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Admin />
                  </ProtectedRoute>
                } />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="*" element={<Navigate to="/events" replace />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </div>
      )}
    </ThemeProvider>
  );
}

export default App; 